import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import { Component, Prop } from 'vue-property-decorator';
import EntityTableProvider from '@shared/components/tables/entity-table-provider/entity-table-provider.vue';
import EntityTable from '@shared/components/tables/entity-table/entity-table.vue';
import BpcPageTitle from '@shared/layouts/components/page-title/page-title.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import EntityDeleteModalProvider from '@shared/components/modals/entity-delete-modal-provider/entity-delete-modal-provider.vue';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import { ValidationObserver } from 'vee-validate';
import { mixins } from 'vue-class-component';
import AbstractTable from '@shared/components/tables/abstract-table/abstract-table';
import StdFilter from '@shared/components/filters/std-filter/std-filter.vue';
import DrawerModal from '@shared/components/modals/drawer-modal/drawer-modal.vue';
import CustomFilterSelector from '@shared/components/filters/custom-filter-selector/custom-filter-selector.vue';
import ExtendedFilter from '@shared/components/filters/extended-filter/extended-filter.vue';
import { Coda } from '@modules/accounting/interfaces/coda';
import BpcImportCodaModal from '@accounting/components/modals/import-coda-modal/import-coda-modal.vue';
import ImportCodaModal from '@accounting/components/modals/import-coda-modal/import-coda-modal.vue';
import BulkMoveToTrashModal from '@shared/components/modals/bulk-move-to-trash-modal/bulk-move-to-trash-modal.vue';
import BulkRecoverModal from '@shared/components/modals/bulk-recover-modal/bulk-recover-modal.vue';

@Component({
  name: 'bpc-coda-table',
  components: {
    'bpc-entity-table-provider': EntityTableProvider,
    'bpc-entity-table': EntityTable,
    'bpc-page-title': BpcPageTitle,
    'font-awesome-icon': FontAwesomeIcon,
    'bpc-entity-delete-modal-provider': EntityDeleteModalProvider,
    'bpc-confirmation-modal': ConfirmationModal,
    'bpc-std-form': StdForm,
    'bpc-std-filter': StdFilter,
    ValidationObserver,
    'bpc-drawer-modal': DrawerModal,
    'bpc-custom-filter-selector': CustomFilterSelector,
    'bpc-extended-filter': ExtendedFilter,
    'bpc-import-coda-modal': BpcImportCodaModal,
    'bpc-bulk-move-to-trash-modal': BulkMoveToTrashModal,
    'bpc-bulk-recover-modal': BulkRecoverModal,
  },
})
export default class CodaTable extends mixins(AbstractTable) {
  declare $refs: AbstractTable['$refs'] & {
    importCodaModal: InstanceType<typeof ImportCodaModal> & {
      onOpenModal(): void;
      onCloseModal(): void;
    };
  };

  @Prop(String) readonly displayConfigName: string;
  @Prop(String) readonly title: string;
  @Prop(String) readonly subtitle: string;

  public entity: Coda = null;
  public entityName = 'coda';
  public pluralEntityName = 'codas';
  public permissionEntityNameToUse = 'codas';

  public flagsToCheckBeforeLoadEntities: string[] = [
    'configLoaded',
    'filtersLoaded',
    'paginationLoaded',
    'orderLoaded',
    'urlParametersLoaded',
  ];

  public customPageActions = {
    importCodaFile: {
      activated: false,
      type: 'action',
      icon: 'file-invoice',
      variant: 'primary',
      text: 'import_file',
      show: false,
      event: 'import-file',
      selectElements: false,
      tag: 'left',
      permissions: ['store'],
    },
  };

  extendUpdateRowActions(entity: any) {
    if (this.tableIsNotFilteredByTrashedElements() && entity.status != 'to_process') {
      //entity.actions.softDelete.show = false;
      entity.actions.edit.show = false;
    }
    if (
      this.tableIsNotFilteredByTrashedElements() &&
      entity.status !== 'to_process' &&
      entity.status !== 'failed'
    ) {
      entity.actions.softDelete.show = false;
    }
  }

  onShow(e) {
    this.onEdit(e, true);
  }

  extendUpdatePageActions() {
    if (this.config) {
      for (const activatedPageAction of this.config.data.activatedPageActions) {
        if (this.customPageActions[activatedPageAction]) {
          this.customPageActions[activatedPageAction].activated = true;
        }
      }
    }

    this.customPageActions.importCodaFile.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed;

    // Check Permissions
    for (const activatedPageAction of this.config.data.activatedPageActions) {
      if (
        this.customPageActions[activatedPageAction] &&
        this.customPageActions[activatedPageAction]['permissions']
      ) {
        this.checkPermission(activatedPageAction);
      }
    }
  }

  customOnPageAction($event: string) {
    switch ($event) {
      case 'import-file':
        this.$refs.importCodaModal.onOpenModal();
        //this.onGenerateInvoices();
        break;
    }
  }

  mounted() {
    this.listenToWebsocketImportCodaFileChannel();
  }

  destroyed() {
    this.leaveToWebsocketImportCodaFileChannel();
  }

  isImportingCodaFile: boolean = false;
  codaFileImported: boolean = false;
  errorImportingCodaFile: boolean = false;
  totalCodaTransactions: number = 0;
  totalCodaTransactionsImported: number = 0;
  totalCodaTransactionsIgnored: number = 0;
  importCodaAlertKey: number = 0;

  get totalTransactionsProcessed(): number {
    return this.totalCodaTransactionsImported + this.totalCodaTransactionsIgnored;
  }

  get importCodaAlertStatus(): string {
    if (this.isImportingCodaFile) {
      return 'warning';
    } else if (this.codaFileImported) {
      return 'success';
    } else if (this.errorImportingCodaFile) {
      return 'danger';
    } else {
      return 'info';
    }
  }

  onStartCodaImport(): void {
    this.importCodaAlertKey = this.importCodaAlertKey + 1;
    this.codaFileImported = false;
    this.errorImportingCodaFile = false;
    this.isImportingCodaFile = true;
    this.totalCodaTransactions = 0;
    this.totalCodaTransactionsImported = 0;
    this.totalCodaTransactionsIgnored = 0;
    this.$refs.importCodaModal.onCloseModal();
  }

  listenToWebsocketImportCodaFileChannel(): void {
    this.$bpcEcho
      .listenImportCodaFileChannel()
      .listen('.BPC\\Accounting\\Events\\ImportCodaFileStarted', data => {
        this.codaFileImported = false;
        this.errorImportingCodaFile = false;
        this.isImportingCodaFile = true;
        this.totalCodaTransactions = data.totalTransactions || 0;
      })
      .listen('.BPC\\Accounting\\Events\\ImportedCodaTransaction', data => {
        this.isImportingCodaFile = !this.codaFileImported;
        this.totalCodaTransactions = data.result?.totalTransactions || 0;
        this.totalCodaTransactionsImported = data.result?.totalImportedTransactions || 0;
        this.totalCodaTransactionsIgnored = data.result?.totalIgnoredTransactions || 0;
      })
      .listen('.BPC\\Accounting\\Events\\CodaTransactionNotProcessed', data => {
        this.isImportingCodaFile = !this.codaFileImported;
        this.totalCodaTransactions = data.result?.totalTransactions || 0;
        this.totalCodaTransactionsImported = data.result?.totalImportedTransactions || 0;
        this.totalCodaTransactionsIgnored = data.result?.totalIgnoredTransactions || 0;
      })
      .listen('.BPC\\Accounting\\Events\\ImportedCodaFile', data => {
        this.codaFileImported = true;
        this.isImportingCodaFile = false;
        this.totalCodaTransactions = data.result?.totalTransactions || 0;
        this.totalCodaTransactionsImported = data.result?.totalImportedTransactions || 0;
        this.totalCodaTransactionsIgnored = data.result?.totalIgnoredTransactions || 0;
        this.$refs.provider.getEntities();
      })
      .listen('.BPC\\Accounting\\Events\\ErrorImportingCodaFile', data => {
        this.errorImportingCodaFile = true;
        this.isImportingCodaFile = false;
      });
  }

  leaveToWebsocketImportCodaFileChannel(): void {
    this.$bpcEcho.leaveImportCodaFileChannel();
  }

  // Bulk soft delete modal (move to trash)
  showBulkMoveToTrashModal = false;
  onBulkSoftDelete() {
    this.showBulkMoveToTrashModal = true;
  }
  onCLoseBulkSoftDelete() {
    this.showBulkMoveToTrashModal = false;
  }
  onSuccessSoftDelete() {
    this.$refs.softDeleteConfirmationModal.onCloseModal();
    this.$refs.provider.getEntities();
  }
  // Here, put the same fields as you put in dataToShowForBulkTrashedEntity in your entity Service
  bulkMoveToTrashDataToShow = [['id', 'status', 'bank_account_number']];

  // Bulk restore modal (recover)
  showBulkRecoverModal = false;

  onBulkRecover() {
    this.showBulkRecoverModal = true;
  }

  onCLoseBulkRecover() {
    this.showBulkRecoverModal = false;
  }

  onSuccessBulkRecover() {
    this.$refs.provider.getEntities();
  }

  bulkRecoverDataToShow = [['id', 'status', 'bank_account_number']];
}
